import { api } from "./client";

/** PrepAI — `/api/admin/metrics/*`, leadership only. */

export type Range = 7 | 30 | 90;
export type MetricStatus = "live" | "needs_tracking";

export interface Kpi {
  key: string;
  label: string;
  value: number | null;
  previous: number | null;
  change: number | null;
  unit: "count" | "ngn" | "percent";
  status: MetricStatus;
}

export interface Overview {
  from: string;
  to: string;
  updated_at: string | null;
  kpis: Kpi[];
  funnel: { key: string; label: string; value: number | null; status: MetricStatus }[];
}

export interface Learning {
  from: string;
  to: string;
  updated_at: string | null;
  daily_active: { date: string; value: number | null }[];
  by_subject: { key: string; value: number | null }[];
  cohorts: { cohort: string; size: number | null; retention: (number | null)[] }[];
  engagement: {
    questions_per_active_day: number | null;
    tutor_rate: number | null;
    session_completion: number | null;
    consent_approval: number | null;
  };
}

export interface Revenue {
  from: string;
  to: string;
  updated_at: string | null;
  daily: { date: string; payments: number; revenue: number }[];
  total_revenue: number;
  total_payments: number;
  outcomes: { key: string; value: number | null }[];
  methods: { key: string; value: number | null }[];
}

export interface ChannelRow {
  key: string;
  /** null: fewer than 5 students, withheld. */
  signups: number | null;
  paid: number | null;
  conversion: number | null;
}

export interface Acquisition {
  from: string;
  to: string;
  updated_at: string | null;
  total_signups: number;
  /** Share of signups that carried any attribution; null with no signups. */
  tracked_share: number | null;
  channels: ChannelRow[];
  campaigns: (Omit<ChannelRow, "key"> & { channel: string; campaign: string })[];
  weeks: { week: string; channels: Record<string, number | null> }[];
}

export interface Operations {
  checked_at: string;
  tiles: {
    key: string;
    label: string;
    value: number | null;
    unit: "count" | "percent" | "seconds";
    state: "healthy" | "watch" | "investigate";
    status: MetricStatus;
    note: string;
    /** Where to look further: the Sentry issues page, the uptime monitor. */
    link?: string;
    linkLabel?: string;
  }[];
  incidents: {
    key: string;
    title: string;
    detail: string;
    opened_at: string;
    resolved_at: string | null;
  }[];
}

export interface TutorProblem {
  turn: number;
  severity: "high" | "medium" | "low";
  category: string;
  description: string;
  suggestion: string;
}

type TutorScores = Record<"correctness" | "relevance" | "clarity" | "teaching" | "tone", number>;

/** How well the AI tutor answers students, from the judge model's grading of each conversation. */
export interface TutorQuality {
  range: number;
  from: string;
  to: string;
  /** Conversations graded in the range. */
  graded: number;
  /** Always 0: the AI engine grades each conversation within seconds, so nothing waits. */
  waiting: number;
  /** Days the engine keeps a conversation's grading (30). A longer range sees only these. */
  kept_days?: number;
  /** True when there were too many gradings to read them all; the newest were counted. */
  truncated?: boolean;
  /** Average 0–100, with the previous range for comparison. */
  score: { value: number | null; previous: number | null };
  /** Share flagged for a person to look at. */
  needs_attention: { value: number | null; previous: number | null };
  /** Average 1–5 per criterion. */
  scores: Record<keyof TutorScores, number | null>;
  problems: Record<TutorProblem["severity"], number>;
  categories: { category: string; count: number }[];
  daily: { date: string; graded: number; score: number | null }[];
  /** The flagged conversations, lowest score first. */
  worst: {
    id: string;
    question_id: string;
    score: number;
    evaluated_at: string;
    question: string | null;
    summary: string;
    problems: TutorProblem[];
    turns: {
      turn: number;
      student: string;
      tutor: string;
      scores: TutorScores;
      score: number;
      comment: string;
    }[];
  }[];
}

const get = <T>(section: string, range?: Range) =>
  api<T>(`/api/admin/metrics/${section}${range ? `?range=${range}` : ""}`);

export const fetchOverview = (range: Range) => get<Overview>("overview", range);
export const fetchLearning = (range: Range) => get<Learning>("learning", range);
export const fetchRevenue = (range: Range) => get<Revenue>("revenue", range);
export const fetchAcquisition = (range: Range) => get<Acquisition>("acquisition", range);
export const fetchOperations = () => get<Operations>("ops");
export const fetchTutorQuality = (range: Range) => get<TutorQuality>("tutor", range);

export interface Me {
  id: string;
  email: string | null;
  firstName: string | null;
  role: string;
  can: { content: boolean; admin: boolean; insights: boolean };
}

export const fetchMe = () => api<Me>("/api/worker/me");
